/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

import { REFRESH_THRESHOLD_MS } from './constants.js';

type RefreshTimerHandle = ReturnType<typeof setTimeout>;

/**
 * Manages the Ecsper context token lifecycle in memory.
 * No refresh tokens. Re-bootstrap via Office SSO when expiring.
 *
 * Source: CTR-PL-MS-OFFICESSO-001 v1.0.0 §5.2 (Q-03: no refresh token;
 *         re-bootstrap via `OfficeRuntime.auth.getAccessToken()`)
 */
export class TokenManager {
  private token: string | null = null;
  private expiresAtMs: number | null = null;
  private refreshTimer: RefreshTimerHandle | null = null;
  private readonly refreshCallbacks = new Set<() => void>();

  /** Store a new context token with its expiry */
  public setToken(token: string, expiresInSeconds: number): void {
    this.clearTimer();
    this.token = token;
    this.expiresAtMs = Date.now() + expiresInSeconds * 1000;
    this.scheduleRefreshTimer();
  }

  /** Get the current token (null if none or expired) */
  public getToken(): string | null {
    if (this.token === null || this.expiresAtMs === null) return null;
    if (this.expiresAtMs <= Date.now()) {
      this.clear();
      return null;
    }
    return this.token;
  }

  /** Check whether the token needs refreshing (within {@link REFRESH_THRESHOLD_MS} of expiry) */
  public needsRefresh(): boolean {
    if (this.token === null || this.expiresAtMs === null) return false;
    const remaining = this.expiresAtMs - Date.now();
    if (remaining <= 0) return false;
    return remaining <= REFRESH_THRESHOLD_MS;
  }

  /** Clear stored token (on logout or auth error) */
  public clear(): void {
    this.clearTimer();
    this.token = null;
    this.expiresAtMs = null;
  }

  /** Register a callback for when refresh is needed (called by timer) */
  public onRefreshNeeded(callback: () => void): () => void {
    this.refreshCallbacks.add(callback);
    return () => {
      this.refreshCallbacks.delete(callback);
    };
  }

  private clearTimer(): void {
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  private scheduleRefreshTimer(): void {
    if (this.expiresAtMs === null || this.token === null) return;
    const delayMs = this.expiresAtMs - REFRESH_THRESHOLD_MS - Date.now();
    const safeDelayMs = Math.max(0, delayMs);
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      if (!this.needsRefresh()) return;
      for (const cb of this.refreshCallbacks) cb();
    }, safeDelayMs);
  }
}
